import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useState } from "react";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { pipelineService } from "../../../api/pipeline.service.js";
import { useFranceToParisCopy } from "./useFranceToParisCopy.js";
import styles from "./PipelineBrowser.module.css";

const DEFAULT_BATCH_SIZE = 1000;
const MIN_BATCH_SIZE = 1;
const MAX_BATCH_SIZE = 5000;

function clampBatchSize(value: number): number {
  if (!Number.isFinite(value)) return MIN_BATCH_SIZE;
  return Math.min(Math.max(Math.trunc(value), MIN_BATCH_SIZE), MAX_BATCH_SIZE);
}

function FranceToParisSection() {
  const [batchSize, setBatchSize] = useState(String(DEFAULT_BATCH_SIZE));

  const { data, loading, error, refetch } = useQuery(
    () => pipelineService.getFranceToParisStats(),
    [],
  );

  const { running, progress, error: loopError, start, stop } = useFranceToParisCopy(refetch);

  const handleStart = useCallback(() => {
    start({ batchSize: clampBatchSize(Number(batchSize)) });
  }, [start, batchSize]);

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Copy France → Paris (FR testimonials)</div>
      <div className={styles.contentSectionTitle}>
        Hardcoded mapping due to specific requirements for year 2026. Use France-Paris tab instead
      </div>
      <div className={styles.syncStateRow}>
        <input
          className={styles.filterInput}
          type="number"
          min={MIN_BATCH_SIZE}
          max={MAX_BATCH_SIZE}
          value={batchSize}
          disabled={running}
          onChange={e => setBatchSize(e.target.value)}
          placeholder="Batch size"
          style={{ width: 100 }}
        />
      </div>

      <div className={styles.syncStateRow}>
        {loading ? (
          <LoadingState message="Loading stats..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load stats"
            message={error.message}
            action={{ label: "Retry", onClick: () => refetch() }}
          />
        ) : (
          <>
            <span>
              Total: <strong>{data?.total ?? "-"}</strong> · Copied:{" "}
              <strong>{data?.copied ?? "-"}</strong> · Remaining:{" "}
              <strong>{data?.remaining ?? "-"}</strong> · Unmapped (skipped):{" "}
              <strong>{data?.unmapped ?? "-"}</strong>
            </span>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              disabled={running}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </>
        )}
      </div>

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={running}
          onClick={handleStart}
        >
          {running ? `Working… (batch ${progress.batch})` : "Start"}
        </Button>
        <Button variant="outlined" color="error" size="small" disabled={!running} onClick={stop}>
          Stop
        </Button>
      </div>

      {(running || progress.batch > 0) && (
        <div className={styles.syncStateMessage}>
          Copied {progress.copied} so far, remaining {progress.remaining ?? "-"}
        </div>
      )}

      {loopError && <div className={styles.syncStateMessage}>Error: {loopError}</div>}
    </div>
  );
}

export default FranceToParisSection;
